import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useMemo, useState } from 'react';
import {
  addMonths,
  MAX_MESES_REPARTO,
  avisosVigentes,
  CORTESIA_SIN_LIMITE,
  avisarEscaneosQueQuedan,
  gastoDesdeEscaneo,
  naturalezaDe,
  nombreProveedorLimpio,
  nombresParecidos,
  totalDeFactura,
  vencimientoDe,
  visibleCategory,
  type CatalogPricesProposal,
  type ExpenseCategory,
  type ExpenseDraft,
  type ExpenseEntry,
  type ExpenseInput,
  type PurchaseDocumentType,
} from '@alexiarest/shared';
import { api, isDemoMode } from '../api/client';
import { invalidateCosting, invalidateFinance, useExpenses, useMe, useSaveSupplier, useScanQuota, useScanReceipt, useSettings, useSuppliers } from '../api/hooks';
import { usePeriod } from '../context/PeriodContext';
import { formatMoney, formatMoneyInput, parseDecimal, today } from '../format';
import { ACCEPT_ESCANER, FORMATOS_ESCANER, prepararImagen } from '../imagenEscaner';
import { BuscarOCrear } from './BuscarOCrear';
import { Drawer } from './Drawer';
import { SmartSelect } from './Field';
import { FacturaLineas, enumerar, frasesProductos, useFacturaLineas } from './FacturaLineas';
import { PreguntaReparto, repartoConFecha, type ValorReparto } from './Reparto';
import { etiquetaCategoria, fechaCorta, GRUPOS_CATEGORIA } from './escanerFormato';
import { useToast } from './Toast';
import '../pages/ingredientes.css';
import './escaner.css';
import { ComprarEscaneos } from './ComprarEscaneos';
import { AlexiaAvatar } from './AlexiaAvatar';

/** «2026-09-28» + 30 días → «2026-10-28». */
const sumarDiasA = (fecha: string, dias: number): string =>
  new Date(Date.parse(`${fecha}T00:00:00Z`) + dias * 86_400_000).toISOString().slice(0, 10);

const OPCIONES_IMAGEN = {
  maxLado: 2000,
  maxPdfBytes: 8_000_000,
  errorPdf: 'Ese PDF pesa demasiado. Exporta solo las páginas de la factura (leo hasta 5) o toma una foto.',
  errorTamanyo: 'La imagen es demasiado grande. Prueba a hacer la foto más de cerca.',
};

/**
 * EL ESCÁNER ÚNICO (bloque S · sección 8b, decidido con Alex el 11 oct y
 * revisado en dev el 12 oct 2026). Se sube la factura UNA vez —desde Gastos,
 * Ingredientes, «Por revisar» o Alexia— y al guardar se guarda SIEMPRE el
 * gasto y se aplican los precios que están claros.
 *
 * El escáner ya lo rellenó todo: no se enseñan campos sino un resumen que se
 * lee de un vistazo. El orden de la pantalla (regla 1): aviso de repetida ·
 * resumen · «¿Ya la pagaste?» · productos (primero lo que pide algo) · pie
 * con lo que va a pasar al guardar. «Corregir» abre SOLO cinco campos:
 * proveedor, fecha, total, IVA (en dinero) y categoría. Fijo o variable sale
 * de la categoría; nunca se pregunta.
 *
 * Lo que Alexia no sabe seguro va en ÁMBAR, nunca en verde: un verde
 * equivocado es peor que diez ámbar de más.
 */
export interface EscaneoInicial {
  proposal: ExpenseDraft;
  key?: string;
  draftId?: string;
  documentType?: PurchaseDocumentType;
  catalogPrices?: CatalogPricesProposal;
  catalogDraftId?: string;
  /** El gasto ya se guardó: solo quedan productos por decidir. */
  gastoGuardado?: boolean;
}

export function ScanReceiptDrawer({
  open,
  onClose,
  onGuardado,
  onVariasSubidas,
  initialDraft,
  confirmExpense,
}: {
  open: boolean;
  onClose: () => void;
  /**
   * La factura recién guardada, para que Gastos proponga juntarla con las
   * notas que la esperaban (9 sep 2026, Alex). El escáner no decide nada.
   */
  onGuardado?: (expense: ExpenseEntry) => void;
  /** Se subieron varias fotos a la vez: todas esperan en «Por revisar». */
  onVariasSubidas?: () => void;
  initialDraft?: EscaneoInicial;
  confirmExpense?: (input: ExpenseInput, acceptPossibleDuplicate?: boolean) => Promise<ExpenseEntry>;
}): JSX.Element | null {
  if (!open) return null;
  return <Cuerpo onClose={onClose} onGuardado={onGuardado} onVariasSubidas={onVariasSubidas} initialDraft={initialDraft} confirmExpense={confirmExpense} />;
}

interface Escaneo extends EscaneoInicial {
  nombreFichero?: string;
}

function Cuerpo({
  onClose,
  onGuardado,
  onVariasSubidas,
  initialDraft,
  confirmExpense,
}: {
  onClose: () => void;
  onGuardado?: (expense: ExpenseEntry) => void;
  onVariasSubidas?: () => void;
  initialDraft?: EscaneoInicial;
  confirmExpense?: (input: ExpenseInput, acceptPossibleDuplicate?: boolean) => Promise<ExpenseEntry>;
}): JSX.Element {
  const toast = useToast();
  const quotaQuery = useScanQuota();
  const escanear = useScanReceipt();
  const qc = useQueryClient();
  const suppliers = useSuppliers().data ?? [];
  const saveSupplier = useSaveSupplier();
  const { period } = usePeriod();

  const [escaneo, setEscaneo] = useState<Escaneo | null>(initialDraft ?? null);
  const [leyendo, setLeyendo] = useState<{ hecho: number; de: number } | null>(null);
  const [error, setError] = useState('');
  const [arrastrando, setArrastrando] = useState(false);

  const quota = quotaQuery.data;
  const bloqueado = initialDraft ? false : quota ? quota.sinModulo || quota.exhausted : false;

  /** Lee un archivo; con `soloBorrador` no abre la revisión (subida de varias). */
  const leer = async (blob: Blob): Promise<Escaneo> => {
    const res = await escanear.mutateAsync({ blob });
    return {
      proposal: res.draft,
      key: res.key,
      draftId: res.draftId,
      documentType: res.documentType,
      catalogPrices: res.catalogPrices,
      catalogDraftId: res.catalogDraftId,
    };
  };

  const cargar = async (files: File[]) => {
    setError('');
    if (!files.length) return;
    if (files.length === 1) {
      setLeyendo({ hecho: 0, de: 1 });
      try {
        const blob = await prepararImagen(files[0], OPCIONES_IMAGEN);
        setEscaneo({ ...(await leer(blob)), nombreFichero: files[0].name });
      } catch (e) {
        setError(e instanceof Error ? e.message : 'No pude leer el documento.');
      } finally {
        setLeyendo(null);
      }
      return;
    }
    // Varias a la vez (regla 11): cada una entra en «Por revisar».
    let bien = 0;
    const fallos: string[] = [];
    for (let i = 0; i < files.length; i++) {
      setLeyendo({ hecho: i, de: files.length });
      try {
        await leer(await prepararImagen(files[i], OPCIONES_IMAGEN));
        bien += 1;
      } catch (e) {
        fallos.push(`${files[i].name}: ${e instanceof Error ? e.message : 'no pude leerla'}`);
      }
    }
    setLeyendo(null);
    void qc.invalidateQueries({ queryKey: ['drafts'] });
    if (fallos.length) setError(fallos.join(' · '));
    if (bien) {
      toast.success(`Leí ${bien === 1 ? '1 factura' : `${bien} facturas`}. Te ${bien === 1 ? 'espera' : 'esperan'} en «Por revisar».`);
      if (!fallos.length) { onClose(); onVariasSubidas?.(); }
    }
  };

  const probarEjemplo = async (dosPaginas = false) => {
    setError('');
    setLeyendo({ hecho: 0, de: 1 });
    try {
      const blob = new Blob([], { type: dosPaginas ? 'application/pdf' : 'image/jpeg' });
      setEscaneo({ ...(await leer(blob)), nombreFichero: dosPaginas ? 'factura-de-2-paginas.pdf' : 'factura-de-ejemplo.jpg' });
    } catch (e) {
      setError(e instanceof Error ? e.message : 'No pude leer el documento.');
    } finally {
      setLeyendo(null);
    }
  };

  if (escaneo) {
    return (
      <Revision
        key={`${escaneo.draftId ?? ''}${escaneo.nombreFichero ?? ''}`}
        escaneo={escaneo}
        suppliers={suppliers}
        onClose={onClose}
        onGuardado={onGuardado}
        confirmExpense={confirmExpense}
        crearProveedor={async (name, taxId) => (await saveSupplier.mutateAsync({ input: { name, ...(taxId ? { taxId } : {}) } })).supplierId}
        periodo={period}
      />
    );
  }

  const inputId = 'escaner-fichero';
  const probar = isDemoMode && (
    <>
      <button type="button" className="btn btn--ghost btn--sm" onClick={() => void probarEjemplo()}>
        Probar con una factura de ejemplo
      </button>
      <button type="button" className="btn btn--ghost btn--sm" onClick={() => void probarEjemplo(true)}>
        Probar con un PDF de 2 páginas
      </button>
    </>
  );

  return (
    <Drawer open title="Subir factura" subtitle="Alexia la lee, guarda el gasto y te enseña qué pasa con cada producto. Tú solo miras lo que está en ámbar." onClose={onClose} width="35rem">
      <div className="stack">
        {quota && bloqueado && (
          <div className="alerta">
            <p>{quota.message ?? 'Ya no te quedan escaneos este mes.'}</p>
            {/* Precios del 15 oct 2026: al 100 %, comprar 150 más (solo si ya paga). */}
            {quota.canBuyPack && <ComprarEscaneos moneda={quota.billingCurrency} variante="principal" />}
          </div>
        )}
        {/* El contador solo al llegar al 80 % del plan (revisión del 14 oct 2026). */}
        {quota && !bloqueado && avisarEscaneosQueQuedan(quota) && (
          <p className="field__hint">
            Te {quota.remaining === 1 ? 'queda' : 'quedan'} <strong>{quota.remaining}</strong> {quota.extension ? 'páginas en la extensión' : `${quota.remaining === 1 ? 'escaneo' : 'escaneos'} este mes`}.
            {quota.canBuyPack && <> <ComprarEscaneos moneda={quota.billingCurrency} variante="enlace" /></>}
          </p>
        )}

        {!bloqueado && (
          <div
            className={`ing-subir${arrastrando ? ' is-arrastrando' : ''}`}
            onDragOver={(e) => { e.preventDefault(); setArrastrando(true); }}
            onDragLeave={() => setArrastrando(false)}
            onDrop={(e) => {
              e.preventDefault();
              setArrastrando(false);
              void cargar(Array.from(e.dataTransfer.files ?? []));
            }}
          >
            {/* Mientras lee, el icono deja sitio a Alexia leyendo (estado «pensando»). */}
            {leyendo ? (
              <span className="ing-subir__icono ing-subir__icono--alexia" aria-hidden="true"><AlexiaAvatar estado="pensando" /></span>
            ) : (
              <span className="ing-subir__icono" aria-hidden="true">
                <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M14 3H6a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V9z" /><path d="M14 3v6h6M8 13h8M8 17h5" /></svg>
              </span>
            )}
            <strong className="ing-subir__titulo">
              {leyendo ? (leyendo.de > 1 ? `Leyendo ${leyendo.hecho + 1} de ${leyendo.de}…` : 'Leyendo…') : 'Arrastra aquí la factura'}
            </strong>
            <p className="ing-subir__texto">
              Una foto, un PDF o una captura. También notas y tickets. Si tienes varias, súbelas juntas: cada una queda en «Por revisar».
            </p>
            <div className="ing-subir__botones">
              <input
                id={inputId}
                type="file"
                multiple
                accept={ACCEPT_ESCANER}
                className="sr-only"
                disabled={Boolean(leyendo)}
                onChange={(e) => {
                  const files = Array.from(e.target.files ?? []);
                  e.target.value = '';
                  void cargar(files);
                }}
              />
              <label htmlFor={inputId} className="btn ing-btn-icono">
                <svg aria-hidden="true" width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M4 8h3l2-3h6l2 3h3v11H4z" /><circle cx="12" cy="13" r="3.5" /></svg>
                Tomar una foto
              </label>
              <label htmlFor={inputId} className="btn btn--ghost">Elegir archivo</label>
            </div>
            {probar && <div className="ing-subir__probar">{probar}</div>}
            <p className="ing-subir__formatos">{FORMATOS_ESCANER}</p>
          </div>
        )}

        {error && <p className="alerta alerta--error">{error}</p>}

        {!bloqueado && (
          <>
            <div className="ing-consejos">
              <h3>Para que salga bien</h3>
              <ul>
                <li>Que se vea la factura entera y con luz.</li>
                <li>Que se lean bien los precios y las cantidades.</li>
                <li>También sirve un ticket de supermercado o una nota.</li>
              </ul>
            </div>
            <CorreoDeAlexia />
          </>
        )}
      </div>
    </Drawer>
  );
}

/* ------------------------------------------------------------------ */
/* «Revisa la factura»                                                 */
/* ------------------------------------------------------------------ */

function Revision({
  escaneo,
  suppliers,
  onClose,
  onGuardado,
  confirmExpense,
  crearProveedor,
  periodo,
}: {
  escaneo: Escaneo;
  suppliers: Array<{ supplierId: string; name: string; taxId?: string; creditDays?: number; archived?: boolean }>;
  onClose: () => void;
  onGuardado?: (expense: ExpenseEntry) => void;
  confirmExpense?: (input: ExpenseInput, acceptPossibleDuplicate?: boolean) => Promise<ExpenseEntry>;
  crearProveedor: (name: string, taxId?: string) => Promise<string>;
  periodo: { start: string; end: string };
}): JSX.Element {
  const toast = useToast();
  const qc = useQueryClient();
  const [draft, setDraft] = useState<ExpenseDraft>(escaneo.proposal);
  const [esNota, setEsNota] = useState(Boolean(escaneo.proposal.esNota || escaneo.documentType === 'albaran'));
  const [repetidaAsumida, setRepetidaAsumida] = useState(false);
  const [corrigiendo, setCorrigiendo] = useState(false);
  const [totalTxt, setTotalTxt] = useState(formatMoneyInput(totalDeFactura(escaneo.proposal)));
  const [ivaTxt, setIvaTxt] = useState(formatMoneyInput(escaneo.proposal.taxAmount ?? 0));
  const vence = vencimientoDe(draft, { suppliers, expenses: [] });
  const [pagada, setPagada] = useState<boolean>(vence.dueDate ? false : escaneo.proposal.paid);
  /*
   * «Vence el» ya no sale el mismo día de la factura (revisión del 14 oct
   * 2026): sin vencimiento en la factura ni días del proveedor, se pregunta
   * «¿Cuántos días te da para pagar?» y la respuesta se guarda en el
   * proveedor. La fecha a mano queda vacía hasta que la elijas.
   */
  const [venceEl, setVenceEl] = useState<string>(vence.dueDate ?? '');
  const [diasTxt, setDiasTxt] = useState('');
  const diasCredito = Math.round(parseDecimal(diasTxt, 0));
  const venceDeDias = diasCredito > 0 ? sumarDiasA(draft.date, diasCredito) : undefined;
  const guardarProveedor = useSaveSupplier();
  const [guardando, setGuardando] = useState(false);
  /** Gasto ya guardado en un intento anterior: reintentar no lo duplica. */
  const [gastoGuardado, setGastoGuardado] = useState<ExpenseEntry | null>(null);
  const lineas = useFacturaLineas(escaneo.catalogPrices);
  const soloProductos = Boolean(escaneo.gastoGuardado);
  // Fijo o variable lo dice la categoría (Configuración › Gastos y compras).
  const settings = useSettings();
  const pais = useMe().data?.tenant?.country;
  // Bloque R: «¿Este pago cubre varios meses?» (seguro anual, aguinaldo…), solo cuando toca.
  const [valorReparto, setValorReparto] = useState<ValorReparto>({});
  const desdeHistorial = `${addMonths(today().slice(0, 7), -MAX_MESES_REPARTO)}-01`;
  const periodoHistorial = useMemo(() => ({ start: desdeHistorial, end: today() }), [desdeHistorial]);
  const historial = useExpenses(periodoHistorial);

  const proveedor = draft.supplierId ? suppliers.find((s) => s.supplierId === draft.supplierId) : undefined;
  const nombreProveedor = proveedor?.name ?? (draft.supplierName ? nombreProveedorLimpio(draft.supplierName) : '');
  const proveedorNuevo = !proveedor && Boolean(nombreProveedor);
  const total = totalDeFactura(draft);
  const iva = esNota ? 0 : draft.taxAmount ?? Math.max(0, Math.round((total - draft.netAmount) * 100) / 100);
  const sinIva = esNota ? total : draft.netAmount;
  const varios = !esNota && (draft.taxMode === 'mixed' || (draft.vatBreakdown?.length ?? 0) > 1);
  const repetida = draft.duplicado && !repetidaAsumida;
  const porDecidir = lineas.resumen.porDecidir;

  /* --- Corregir: los cinco campos --- */
  const cambiarTotal = (texto: string) => {
    setTotalTxt(texto);
    const t = parseDecimal(texto);
    setDraft((d) => {
      const tax = esNota ? 0 : d.taxAmount ?? 0;
      const net = Math.round((t - tax) * 100) / 100;
      return { ...d, invoiceTotal: t, totalLeido: t, netAmount: net, subtotalBeforeDiscount: undefined, discountAmount: undefined, vatBreakdown: undefined, vatNote: undefined, vatPct: net > 0 ? Math.round((tax / net) * 1_000_000) / 10_000 : 0 };
    });
  };
  const cambiarIva = (texto: string) => {
    setIvaTxt(texto);
    if (!/\d/.test(texto)) return;
    const tax = parseDecimal(texto);
    setDraft((d) => {
      const t = totalDeFactura(d);
      const net = Math.round((t - tax) * 100) / 100;
      return { ...d, taxAmount: tax, vatAmount: tax, invoiceTotal: t, netAmount: net, subtotalBeforeDiscount: undefined, discountAmount: undefined, vatBreakdown: undefined, vatNote: undefined, taxMode: 'single', vatPct: net > 0 ? Math.round((tax / net) * 1_000_000) / 10_000 : 0 };
    });
  };
  const cambiarCategoria = (category: ExpenseCategory) =>
    setDraft((d) => ({ ...d, category, nature: naturalezaDe(category, settings.data), categoriaDelProveedor: false, confianza: { ...d.confianza, categoria: 'alta' } }));

  /* --- Guardar --- */
  const guardar = async () => {
    if (repetida) return;
    if (!soloProductos && !(total > 0)) { toast.notify('Escribe el total antes de guardar.', 'error'); setCorrigiendo(true); return; }
    if (!soloProductos && sinIva <= 0) { toast.notify('El IVA no puede ser mayor que el total.', 'error'); setCorrigiendo(true); return; }
    setGuardando(true);
    try {
      // 1. Proveedor nuevo: primero se busca como en Ingredientes; si no está, se crea con su nombre limpio y su RFC/NIF.
      let supplierId = draft.supplierId;
      if (!supplierId && nombreProveedor) {
        const existente = suppliers.find((s) => !s.archived && nombresParecidos(s.name, nombreProveedor));
        supplierId = existente?.supplierId ?? await crearProveedor(nombreProveedor, draft.supplierTaxId);
        setDraft((d) => ({ ...d, supplierId }));
      }
      const concepto = nombreProveedor || draft.concept;

      // 2. El gasto, SIEMPRE (salvo que ya estuviera guardado).
      let gasto = gastoGuardado;
      if (!soloProductos && !gasto) {
        const input: ExpenseInput = {
          ...gastoDesdeEscaneo({ ...draft, supplierId, esNota, ...(esNota ? { netAmount: total, taxAmount: 0 } : {}) }, {
            documentType: esNota ? 'albaran' : 'factura',
            paid: pagada,
            dueDate: pagada ? undefined : vence.dueDate ?? venceDeDias ?? (venceEl || undefined),
            attachmentKey: escaneo.key && !escaneo.key.startsWith('demo/') ? escaneo.key : undefined,
            concept: concepto,
          }),
          // Bloque R: el reparto entre meses (o «no es gasto») se guarda con el gasto.
          ...(valorReparto.reparto ? { reparto: valorReparto.reparto } : {}),
          ...(valorReparto.noEsGasto ? { noEsGasto: valorReparto.noEsGasto } : {}),
        };
        if (confirmExpense) gasto = await confirmExpense(input, repetidaAsumida);
        else if (escaneo.draftId) {
          const confirmado = await api.confirmDraft(escaneo.draftId, { expense: input, acceptPossibleDuplicate: repetidaAsumida });
          if (!confirmado.result?.expenseId) throw new Error('El gasto no se guardó.');
          gasto = await api.getExpense(confirmado.result.expenseId);
        } else gasto = await api.createExpense(input);
        setGastoGuardado(gasto);
        // «¿Cuántos días te da para pagar?»: se recuerda en el proveedor para la próxima.
        if (!pagada && !vence.dueDate && diasCredito > 0 && supplierId) {
          await guardarProveedor
            .mutateAsync({ id: supplierId, input: { name: proveedor?.name ?? nombreProveedor, creditDays: diasCredito } })
            .catch((e) => console.warn('[escaner] días de crédito sin guardar', e));
        }
      }

      // 3. Los precios claros; lo que falta se queda en «Por revisar» dentro de esta factura.
      const { errores, quedan } = await lineas.aplicar(supplierId);
      if (escaneo.catalogDraftId) {
        await api.dejarLineasPorDecidir(escaneo.catalogDraftId, quedan, {
          proveedor: concepto || 'Factura', ...(supplierId ? { supplierId } : {}), total, fecha: draft.date,
          ...(gasto ? { expenseId: gasto.entryId } : {}), ...(esNota ? { esNota: true } : {}),
        }).catch((e) => console.warn('[escaner] por revisar sin actualizar', e));
      }
      void qc.invalidateQueries({ queryKey: ['drafts'] });
      // El gasto mueve la P&L, lo que debes, los KPIs…; los precios aplicados, el costeo.
      invalidateFinance(qc);
      if (lineas.lineas.length > quedan.length) invalidateCosting(qc);
      if (errores.length) { toast.notify(`El gasto quedó guardado, pero no pude con algunos precios: ${errores.join(' · ')}`, 'error'); return; }

      const fuera = !soloProductos && (draft.date < periodo.start || draft.date > periodo.end);
      const partes = [
        soloProductos ? 'Listo.' : esNota ? 'Nota guardada.' : 'Gasto guardado.',
        quedan.length ? `${quedan.length === 1 ? 'El producto por decidir te espera' : `Los ${quedan.length} productos por decidir te esperan`} en «Por revisar».` : '',
        fuera ? `Es del ${fechaCorta(draft.date)}: no lo ves en la tabla porque está fuera del periodo que tienes elegido.` : '',
      ];
      toast.success(partes.filter(Boolean).join(' '));
      onClose();
      if (gasto && !esNota) onGuardado?.(gasto);
    } catch (e) {
      toast.fail(e);
    } finally {
      setGuardando(false);
    }
  };

  const noGuardar = async () => {
    for (const id of [escaneo.draftId, escaneo.catalogDraftId]) {
      if (id) await api.discardDraft(id).catch(() => undefined);
    }
    void qc.invalidateQueries({ queryKey: ['drafts'] });
    toast.success('No la guardé: ya la tenías.');
    onClose();
  };

  const dejar = () => {
    if (escaneo.draftId || escaneo.catalogDraftId) toast.success('La dejé en «Por revisar».');
    onClose();
  };

  /* --- Pie: lo que va a pasar al guardar (regla 8) --- */
  const productos = frasesProductos(lineas.resumen);
  const elGasto = soloProductos ? '' : esNota ? `se guarda la nota (${formatMoney(total)})` : `se guarda el gasto (${formatMoney(sinIva)} sin IVA)`;
  const pie = porDecidir > 0
    ? `Al guardar: ${[elGasto, 'los precios que ya están claros'].filter(Boolean).join(' y ')}. ${porDecidir === 1 ? 'El producto por decidir te espera' : `Los ${porDecidir} productos por decidir te esperan`} en «Por revisar».`
    : `Al guardar: ${enumerar([elGasto, ...productos].filter(Boolean)) || 'no cambia nada'}.${lineas.resumen.actualizan + lineas.resumen.crean > 0 ? ' Tus recetas se recalculan solas.' : ''}`;

  const sinNumero = !esNota && !soloProductos && !draft.invoiceNumber;
  const deCredito = vence.de === 'proveedor' && proveedor ? `${proveedor.name} te da ${vence.dias} días.`
    : vence.de === 'factura' ? 'La fecha de vencimiento la dice la factura.' : '';

  return (
    <Drawer
      open
      title="Revisa la factura"
      subtitle="Esto es lo que leí. Solo tienes que mirar lo que está en ámbar."
      onClose={dejar}
      width="35rem"
      footer={
        <div className="esc-pie">
          <p>{pie}</p>
          <div className="esc-pie__botones">
            <button type="button" className="btn" disabled={guardando || Boolean(repetida)} onClick={() => void guardar()}>
              {guardando ? 'Guardando…' : 'Guardar'}
            </button>
            <button type="button" className="btn btn--ghost" onClick={dejar}>Dejarla para después</button>
          </div>
        </div>
      }
    >
      <div className="esc">
        {/* (a) Repetida: arriba del todo, en rojo, antes que nada. */}
        {repetida && draft.duplicado && (
          <div className="esc-repetida" role="alert">
            <p>
              <strong>Parece que ya la tienes.</strong>{' '}
              {[draft.duplicado.existente.concept, draft.duplicado.existente.invoiceNumber].filter(Boolean).join(', ')}, del {fechaCorta(draft.duplicado.existente.date)} por{' '}
              {formatMoney(totalDeFactura(draft.duplicado.existente))}. Si la guardas otra vez, contaría doble.
            </p>
            <div className="esc-repetida__botones">
              <button type="button" className="btn esc-btn-rojo" onClick={() => void noGuardar()}>Es la misma: no guardar</button>
              <button type="button" className="btn btn--ghost" onClick={() => setRepetidaAsumida(true)}>Es otra factura</button>
            </div>
          </div>
        )}

        {/* (b) Resumen de un vistazo. */}
        <section className="esc-resumen" aria-label="Resumen de la factura">
          <div className="esc-resumen__fila esc-resumen__fila--grande">
            <span className="esc-resumen__proveedor">
              {nombreProveedor || <span className="esc-ambar-texto">No veo el proveedor</span>}
              {proveedorNuevo && <span className="esc-resumen__nuevo"> · proveedor nuevo</span>}
            </span>
            <span className="esc-resumen__total">{formatMoney(total)}</span>
          </div>
          <div className="esc-resumen__fila">
            <span>{esNota ? `Nota del ${fechaCorta(draft.date)}` : `Factura del ${fechaCorta(draft.date)}`}</span>
            <span>{soloProductos ? 'El gasto ya está guardado' : esNota ? 'Nota sin factura · cuenta el total' : <>IVA {formatMoney(iva)} · sin IVA <strong>{formatMoney(sinIva)}</strong></>}</span>
          </div>
          {varios && !soloProductos && <p className="esc-gris">La factura trae varios tipos de IVA: tomé el total que dice ({formatMoney(iva)}).</p>}
          {!esNota && (draft.discountAmount ?? 0) > 0.009 && <p className="esc-gris">Incluye un descuento de {formatMoney(draft.discountAmount)}.</p>}
          {esNota && !soloProductos && (
            <p className="esc-gris">
              No veo datos fiscales: la cuento como nota el día de la nota. {''}
              <button type="button" className="esc-enlace" onClick={() => { setEsNota(false); setCorrigiendo(true); }}>Es una factura</button>
            </p>
          )}
          {avisosVigentes(draft.avisos).map((a, i) => <p key={i} className="esc-ambar-texto esc-resumen__aviso">{a}</p>)}
          {!soloProductos && <p className={`esc-resumen__categoria${draft.confianza.categoria !== 'alta' ? ' esc-ambar-texto' : ''}`}>
            {etiquetaCategoria(draft.category)}
            {draft.categoriaDelProveedor ? ' · como sus otras facturas' : draft.confianza.categoria !== 'alta' ? ' · compruébala' : ''}
          </p>}
          {sinNumero && (
            <div className="esc-numero">
              <label htmlFor="esc-numero">No veo el número de factura. Escríbelo y te aviso si un día la subes repetida.</label>
              <input id="esc-numero" className="input" placeholder="Ej. F-234" maxLength={40} value={draft.invoiceNumber ?? ''} onChange={(e) => setDraft((d) => ({ ...d, invoiceNumber: e.target.value || undefined }))} />
            </div>
          )}
          {!soloProductos && (
            <button type="button" className="esc-enlace esc-resumen__corregir" aria-expanded={corrigiendo} onClick={() => setCorrigiendo((v) => !v)}>
              {corrigiendo ? 'Listo' : 'Corregir proveedor, fecha, importes o categoría'}
            </button>
          )}
          {corrigiendo && !soloProductos && (
            <div className="esc-corregir">
              <div className="esc-corregir__fila">
                <span className="esc-corregir__lbl">Proveedor</span>
                <BuscarOCrear
                  value={draft.supplierId ?? ''}
                  ariaLabel="Proveedor"
                  placeholder={nombreProveedor || 'Escribe para buscar…'}
                  opciones={suppliers.filter((s) => !s.archived).map((s) => ({ value: s.supplierId, label: s.name }))}
                  onElegir={(supplierId) => setDraft((d) => ({ ...d, supplierId: supplierId || undefined, supplierName: supplierId ? undefined : d.supplierName }))}
                  onCrear={(texto) => setDraft((d) => ({ ...d, supplierId: undefined, supplierName: texto.trim() }))}
                />
              </div>
              <label className="esc-corregir__fila">
                <span className="esc-corregir__lbl">Fecha</span>
                <input className="input" type="date" value={draft.date} max={today()} onChange={(e) => {
                    const v = e.target.value;
                    if (!v) return;
                    const antes = draft.date;
                    setDraft((d) => ({ ...d, date: v }));
                    // El reparto se mueve con la fecha.
                    setValorReparto((prev) => {
                      const reparto = repartoConFecha(prev.reparto, antes, v);
                      return reparto === prev.reparto ? prev : { ...prev, reparto };
                    });
                  }} />
              </label>
              <label className="esc-corregir__fila">
                <span className="esc-corregir__lbl">Total de la factura</span>
                <input className="input esc-num" inputMode="decimal" value={totalTxt} onChange={(e) => cambiarTotal(e.target.value)} />
              </label>
              {!esNota && (
                <label className="esc-corregir__fila">
                  <span className="esc-corregir__lbl">IVA de la factura</span>
                  <input className="input esc-num" inputMode="decimal" value={ivaTxt} onChange={(e) => cambiarIva(e.target.value)} />
                </label>
              )}
              <div className="esc-corregir__fila">
                <span className="esc-corregir__lbl">Categoría</span>
                <SmartSelect ariaLabel="Categoría" value={visibleCategory(draft.category)} groups={GRUPOS_CATEGORIA} onChange={(v) => v && cambiarCategoria(v as ExpenseCategory)} />
              </div>
            </div>
          )}
        </section>

        {/* (c) La única pregunta fija. */}
        {!soloProductos && (
          <div className="esc-pago" role="group" aria-labelledby="esc-pago-lbl">
            <span id="esc-pago-lbl" className="esc-pago__lbl">¿Ya la pagaste?</span>
            <div className="esc-pago__botones">
              <button type="button" className={`esc-opcion${pagada ? ' is-activa' : ''}`} aria-pressed={pagada} onClick={() => setPagada(true)}>Sí</button>
              <button type="button" className={`esc-opcion${!pagada ? ' is-activa' : ''}`} aria-pressed={!pagada} onClick={() => setPagada(false)}>
                {vence.dueDate ? `No, vence el ${fechaCorta(vence.dueDate)}` : 'No, la debo'}
              </button>
            </div>
            {!pagada && !vence.dueDate && (
              <div className="esc-pago__credito">
                <label className="esc-pago__vence">
                  <span>¿Cuántos días te da para pagar?</span>
                  <input
                    className="input esc-pago__dias"
                    inputMode="numeric"
                    placeholder="Ej. 30"
                    aria-label="Días para pagar"
                    value={diasTxt}
                    onChange={(e) => { setDiasTxt(e.target.value.replace(/[^\d]/g, '').slice(0, 3)); if (e.target.value) setVenceEl(''); }}
                  />
                </label>
                {venceDeDias ? (
                  <p className="esc-gris">Vence el {fechaCorta(venceDeDias)}.{nombreProveedor ? ` Lo recuerdo para las próximas de ${nombreProveedor}.` : ''}</p>
                ) : (
                  <label className="esc-pago__vence">
                    <span>o elige la fecha</span>
                    <input className="input" type="date" value={venceEl} min={draft.date} aria-label="Vence el" onChange={(e) => setVenceEl(e.target.value)} />
                  </label>
                )}
              </div>
            )}
            {deCredito && <p className="esc-gris">{deCredito}</p>}
          </div>
        )}

        {/* Bloque R: pago que cubre varios meses. No bloquea: sin contestar, cuenta entero en su mes. */}
        {!soloProductos && (
          <PreguntaReparto
            gasto={{ concept: draft.concept || nombreProveedor, category: draft.category, netAmount: sinIva, date: draft.date, supplierId: draft.supplierId }}
            proveedor={nombreProveedor || undefined}
            historial={historial.data ?? []}
            value={valorReparto}
            onChange={setValorReparto}
            pais={pais}
          />
        )}

        {/* (d) Los productos: primero lo que pide algo. */}
        {lineas.lineas.length > 0 && <FacturaLineas estado={lineas} />}
      </div>
    </Drawer>
  );
}

/**
 * «¿Te llegan por correo?» (rediseño, 11 oct 2026): cada cuenta tiene su
 * correo de Alexia al crearse.
 */
function CorreoDeAlexia(): JSX.Element | null {
  const toast = useToast();
  const query = useQuery({ queryKey: ['inbound-email'], queryFn: () => api.getInboundEmailSettings(), staleTime: 60_000 });
  const address = query.data?.available === false ? undefined : query.data?.address;
  if (!address) return null;
  return (
    <div className="ing-correo">
      <h3>¿Te llegan por email?</h3>
      <p>Reenvíalas a tu correo de Alexia y aparecerán en «Por revisar», listas para mirar.</p>
      <div className="ing-correo__fila">
        <code>{address}</code>
        <button type="button" className="btn btn--ghost" onClick={() => { void navigator.clipboard.writeText(address).then(() => toast.success('Correo copiado.')).catch(toast.fail); }}>Copiar</button>
      </div>
    </div>
  );
}
